// Plain-words "what is happening / do this" for every health alarm, and plain-words error codes.
// Written for the person at the front desk, not for an engineer. Browser-safe, no dependencies.
import type { AlarmAction, HealthEventType } from './types';

/** The four steps that fix most phone problems; reused by several alarms. */
const PHONE_CHECK: readonly string[] = [
  "Find the phone, unlock it, and open the httpSMS app.",
  "Check it has signal, Wi-Fi, and is on the charger.",
  "On the phone: Settings › Apps › httpSMS › Battery › Unrestricted.",
  "Run the self-test from the Health page.",
];

export const ALARM_ACTIONS: Record<HealthEventType, AlarmAction> = {
  heartbeat_gap: {
    what: "The texting phone stopped checking in.",
    doThis: [...PHONE_CHECK],
  },
  queue_depth: {
    what: "Texts are piling up. The phone is not sending them.",
    doThis: [
      ...PHONE_CHECK,
      "If the pile does not shrink in 10 minutes, force-close the httpSMS app on the phone and open it again.",
    ],
  },
  token_missing: {
    what: "The phone lost its link to httpSMS. It looks online but nothing can be sent.",
    doThis: [
      "On the phone, force-close the httpSMS app and open it again.",
      "Do not change anything on the httpSMS website.",
      "Run the self-test.",
    ],
  },
  rate_limit_low: {
    what: "The phone's sending rate is set too low, so texts go out in a flat rhythm.",
    doThis: ["In the httpSMS app on the phone, set the rate to 10 per minute."],
  },
  radio_error: {
    what: "The carrier is refusing texts from this number. Sending is paused.",
    doThis: [
      "Do not send anything else from this number today.",
      "Do not add a second phone.",
      "Call Laz before resuming.",
    ],
  },
  phone_offline: {
    what: "httpSMS reports the phone as offline.",
    doThis: [...PHONE_CHECK],
  },
  repetition: {
    what: "The same text has gone out many times today. Carriers read that as automated.",
    doThis: [
      "Add another wording to the reminder template.",
      "If you are typing the same message by hand, vary the words.",
    ],
  },
  provider_error: {
    what: "The texting provider refused a request from this app.",
    doThis: [
      "Check the API key under Settings › Lines.",
      "Open the failed text in the thread to see the detail.",
    ],
  },
  webhook_config: {
    what: "Replies are not set up to reach this app. Customers' texts would be lost.",
    doThis: [
      "On httpsms.com › Webhooks, set the URL to the one shown on the Health page.",
      "Tick only message.phone.received.",
      "Run the self-test.",
    ],
  },
  inbound_silent: {
    what: "Many texts went out but no reply came in for a whole day. Replies may be getting lost.",
    doThis: ["Run the self-test.", "Check the webhook on httpsms.com."],
  },
  inbound_error: {
    what: "A reply arrived but this app could not process it.",
    doThis: [
      "Open Health › recent events for the detail.",
      "Tell Laz. The reply is stored and can be replayed.",
    ],
  },
  stuck_sending: {
    what: "A text was handed to the phone and nothing came back for 5 minutes.",
    doThis: [
      "Check the phone is on and unlocked.",
      "Open the thread. The text is marked unknown and has a Resend button.",
    ],
  },
  line_paused: {
    what: "This line is paused because the carrier refused a text.",
    doThis: [
      "Wait for the pause to end, or ask an admin to press Resume on the Health page.",
      "Do not add a second phone.",
    ],
  },
};

/**
 * One alarm TYPE can mean two different things, and the DB CHECK on health_events.type is a
 * closed set, so the second meaning travels in the event's `details` instead of a new type.
 * `details.what` replaces the sentence, `details.do_this` replaces the steps (alarmActionFor).
 *
 * queue_depth is the one that needed it (review round 2): texts sitting ON THE PHONE mean the
 * handset is not sending, but texts sitting in OUR queue while the outbox is stalled mean the
 * opposite, and telling the office to force-close httpSMS for that is exactly wrong.
 */
export const OUTBOX_STALLED_ACTION: AlarmAction = {
  what: "Texts are waiting and the app is not handing them to the phone.",
  doThis: [
    "Check the line is switched on and not paused under Settings › Lines.",
    "Open Health › recent events and read the newest one.",
    "Leave the phone alone: it is not the problem. Tell Laz the sending worker has stopped.",
  ],
};

/**
 * The plain words for one alarm ROW: the type's standard text, unless the row carries its own
 * `details.what` / `details.do_this` (see OUTBOX_STALLED_ACTION).
 */
export function alarmActionFor(type: HealthEventType, details?: Record<string, unknown> | null): AlarmAction {
  const base = ALARM_ACTIONS[type] ?? { what: String(type), doThis: [] };
  const what = details && typeof details.what === 'string' ? details.what.trim() : '';
  const steps = details && Array.isArray(details.do_this) ? details.do_this.map((s) => String(s)) : null;
  if (!what && !steps) return base;
  return { what: what || base.what, doThis: steps ?? [...base.doThis] };
}

/** Fixed provider / radio codes and statuses -> plain words. Keys are upper-case. */
export const ERROR_CODE_TEXT: Readonly<Record<string, string>> = {
  LIMIT_EXCEEDED: "Carrier is throttling our number",
  NO_SERVICE: "The phone has no signal",
  RADIO_OFF: "The phone's radio is off (airplane mode?)",
  GENERIC_FAILURE: "The phone could not send it",
  NULL_PDU: "The phone could not build the text",
  MOBILE_APP_INACTIVE: "The httpSMS app on the phone is not running",
  "CANNOT BE DELIVERED": "The carrier could not deliver it",
  SUBMIT_ERROR: "This app could not reach the provider",
  UNKNOWN_SUBMIT: "Unknown. Check the phone.",
  EXPIRED: "The phone was asleep too long",
};

const UNKNOWN_RADIO_TEXT = "Carrier rejected this text";

/**
 * Plain words for a provider error code or message status. `UNKNOWN:*` radio codes all map to
 * "Carrier rejected this text"; null / empty -> ""; anything unrecognised -> the raw code.
 */
export function errorCodeText(codeOrStatus: string | null | undefined): string {
  if (codeOrStatus == null) return "";
  const raw = String(codeOrStatus).trim();
  if (raw === "") return "";
  const key = raw.toUpperCase();
  if (key.startsWith("UNKNOWN:")) return UNKNOWN_RADIO_TEXT;
  return ERROR_CODE_TEXT[key] ?? raw;
}
